import { Injectable, OnDestroy, inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  debounceTime,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  takeUntil,
  finalize,
  from,
  withLatestFrom,
} from 'rxjs';
import { Subject } from 'rxjs';
import { CONST_CITIES, CONST_COUNTRIES, CONST_STATES } from 'paylink-constants';
import {
  Cities,
  City,
  Countries,
  Country,
  PayLinkStepOption,
  State,
  States,
  SubscriptionSignUpOrderSummaryResponse,
  WaiverMasterResponseList,
} from 'models';
import { LinkType, PaidViaPaymentMode, PayLinkSteps, PlanCategory } from 'enum';
import {
  ERROR_COMPANY_DETAILS,
  FETCHED_COMPANY_DETAILS,
  SELECT_LANGUAGE,
} from './actions/company.action';
import {
  FETCHED_SUBSCRIPTION_PAYLOAD,
  ERROR_SUBSCRIPTION_PAYLOAD,
} from './actions/subscription-payload';
import {
  FETCHED_TENANT_DETAILS,
  ERROR_TENANT_LIST,
  STATSIG_UPDATE_USER,
  STATSIG_UPDATE_USER_SUCCESS,
  FETCHED_CURRENT_LOCATION,
  ERROR_CURRENT_LOCATION,
} from './actions/tenant.action';
import {
  FETCH_COUNTRY_MASTER,
  FETCH_COUNTRY_MASTER_LIST,
} from './actions/country.action';
import {
  FETCH_STATE_MASTER,
  FETCH_STATE_MASTER_LIST,
} from './actions/state.action';
import {
  FETCH_CITY_MASTER,
  FETCH_CITY_MASTER_LIST,
} from './actions/city.action';
import { FETCHED_SIGNUPSTEP_INFO } from './actions/signupsteps';
import { COMPANY_INFO } from './app.selector';
import { AppInitService } from '../services/app-init/appinit.service';
import { SubscriptionSignupByUuidService } from '../services/subscription-signup-by-uuid.service';
import { LocationService } from '../services/common/location.service';
import Statsig, { StatsigUser } from 'statsig-js';
import { environment } from '../../environments/environment';
import { OpenapiService } from '../services/openapi/openapi.service';

@Injectable()
export class CompanyEffects implements OnDestroy {
  private destroy$ = new Subject<void>();

  private appInit = inject(AppInitService);
  private actions$ = inject(Actions);
  private store = inject(Store);
  private subscriptionSignUp = inject(SubscriptionSignupByUuidService);
  private locationService = inject(LocationService);
  private router = inject(Router);
  private openApiService = inject(OpenapiService);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    // Constructor kept for potential future initialization
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getLocationState<T>(
    payload: any,
    items: T[],
    matchPredicate: (item: T, payload: any) => boolean,
  ): { items: T[]; selected: T } {
    if (!payload) {
      return { items, selected: {} as T };
    }
    const selected =
      items.find((item) => matchPredicate(item, payload)) || ({} as T);
    return { items, selected };
  }

  getCountryState(tenantPayload: any): Countries {
    const { items: countries, selected: country } = this.getLocationState(
      tenantPayload,
      CONST_COUNTRIES,
      (o: Country, payload) =>
        String(o.three_letter_code).toLowerCase() === String(payload.country).toLowerCase() ||
        String(o.two_letter_code).toLowerCase() === String(payload.country).toLowerCase()
    );
    return { countries, country };
  }

  getStatesState(tenantPayload: any): States {
    const { items: states, selected: state } = this.getLocationState(
      tenantPayload,
      CONST_STATES,
      (o: State, payload) =>
        String(o.code).toLowerCase() === String(payload.state).toLowerCase()
    );
    return { states, state };
  }

  getCitiesState(tenantPayload: any): Cities {
    const { items: cities, selected: city } = this.getLocationState(
      tenantPayload,
      CONST_CITIES,
      (o: City, payload) =>
        String(o.code).toLowerCase() === String(payload.city).toLowerCase()
    );
    return { cities, city };
  }

  getOrderSummary$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType('FETCHED_SUBSCRIPTION_PAYLOAD'),
      switchMap((param: any) => {
        const subscriptionData = this.subscriptionSignUp.GetOrderSummary(
          environment.BASE_URL,
          param.companyUuid,
          param.tenantUuid,
          param.uuid,
        );
        return forkJoin({ subscription: subscriptionData, param: of(param) });
      }),
      mergeMap((obj) => {
        const subscriptionDetails =
          obj.subscription as SubscriptionSignUpOrderSummaryResponse;
        if (subscriptionDetails && subscriptionDetails.tenant_uuid != '') {
          if (
            subscriptionDetails.link_type != LinkType.PaymentMethod &&
            subscriptionDetails.link_type.toString() !=
            LinkType[LinkType.PaymentMethod] &&
            subscriptionDetails.link_type.toString() !=
            LinkType[LinkType.Balance] &&
            subscriptionDetails.link_type != LinkType.Balance
          ) {
            this.store.dispatch({
              type: 'SIGNUPSTEP_INFO',
              companyUuid: subscriptionDetails.company_uuid,
              tenantUuid: subscriptionDetails.tenant_uuid,
              uuid: (obj.param as any).uuid,
              isOffline: subscriptionDetails.is_offline_payment,
              subscriptionDetails: subscriptionDetails,
            });
          } else {
            // No action needed for this branch
          }
          //this.store.dispatch({ type: 'FETCHED_TENANT_LIST', companyUuid: subscriptionDetails.company_uuid, tenantUuid:subscriptionDetails.tenant_uuid });
        }
        return [
          FETCHED_SUBSCRIPTION_PAYLOAD({ orderSummary: subscriptionDetails }),
        ];
      }),
      catchError((err) => {
        if (err.originalError.status == '0')
          return of(
            ERROR_SUBSCRIPTION_PAYLOAD({
              err: { details: { status: '0', errResponse: '' }, status: true },
            }),
          );
        else
          return of(
            ERROR_SUBSCRIPTION_PAYLOAD({
              err: {
                details: {
                  status: err.originalError.status,
                  errResponse: err.originalError.error,
                },
                status: true,
              },
            }),
          );
      }),
    );
  });

  getSignupSteps$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType('SIGNUPSTEP_INFO'),
      takeUntil(this.destroy$),
      switchMap((param: any) => {

        if((param.subscriptionDetails.link_type == LinkType.Freeze || param.subscriptionDetails.link_type.toString() == LinkType[LinkType.Freeze])
           || (param.subscriptionDetails.link_type == LinkType.Charge || param.subscriptionDetails.link_type.toString() == LinkType[LinkType.Charge]))
        {
          return of({waiver: null, mandate: null, param: param});
        }
        const waiverPayload = this.subscriptionSignUp.GetWaivers(
          environment.BASE_URL,
          param.companyUuid,
          param.tenantUuid,
          param.uuid,
        );
        const clientMandatoryFieldPayload =
          this.subscriptionSignUp.GetClientMandatoryField(
            environment.BASE_URL,
            param.companyUuid,
            param.tenantUuid,
            param.uuid,
          );
        return forkJoin({
          waiver: waiverPayload,
          mandate: clientMandatoryFieldPayload,
          param: of(param),
        });
      }),
      mergeMap((stepsPayload) => {
        const steps: PayLinkStepOption[] = [];
        const subsDetails = stepsPayload.param.subscriptionDetails as SubscriptionSignUpOrderSummaryResponse;

        if (subsDetails && (subsDetails.link_type == LinkType.PaymentMethod || subsDetails.link_type.toString() == LinkType[LinkType.PaymentMethod])) {
          steps.push({ name: PayLinkSteps.Payment, isProcessed: false, data: {}, redirectPath: 'payment', isCurrentPage: true } as PayLinkStepOption);
        }
        else if (subsDetails && (subsDetails.link_type == LinkType.Balance || subsDetails.link_type.toString() == LinkType[LinkType.Balance])) {
          steps.push({ name: PayLinkSteps.Payment, isProcessed: false, data: {}, redirectPath: 'payment', isCurrentPage: true } as PayLinkStepOption);
        }
        else if (subsDetails && (subsDetails.link_type == LinkType.Freeze || subsDetails.link_type.toString() == LinkType[LinkType.Freeze])) {
          steps.push({ name: PayLinkSteps.Details, isProcessed: true, data: {}, redirectPath: 'details', isCurrentPage: true } as PayLinkStepOption);
          if (subsDetails.charging_today) {
            steps.push({ name: PayLinkSteps.Payment, isProcessed: false, data: {}, redirectPath: 'payment', isCurrentPage: true } as PayLinkStepOption);
          }
        }
        else {
          steps.push({ name: PayLinkSteps.Details, isProcessed: true, data: {}, redirectPath: 'details', isCurrentPage: true } as PayLinkStepOption);

          const waiver = stepsPayload.waiver as WaiverMasterResponseList;

          if (waiver && waiver.items && waiver.items.length > 0)
            steps.push({ name: PayLinkSteps.Waiver, isProcessed: false, data: waiver.items, redirectPath: 'waiver', isCurrentPage: false } as PayLinkStepOption);

          const clientMandateInfo = stepsPayload.mandate as string[];

          if (clientMandateInfo && clientMandateInfo.length > 0  &&  subsDetails.is_paid)
            steps.push({ name: PayLinkSteps.Information, isProcessed: false, data: clientMandateInfo, redirectPath: 'info', isCurrentPage: false } as PayLinkStepOption);

          if (this.shouldShowPaymentStep(subsDetails)) {
            steps.push({ name: PayLinkSteps.Payment, isProcessed: false, data: {}, redirectPath: 'payment', isCurrentPage: false } as PayLinkStepOption);
          }
        }

        return [FETCHED_SIGNUPSTEP_INFO({ steps: steps })];
      }),
      finalize(() => {
        // Cleanup any pending requests
      }),
      catchError((err) => {
        console.error('Signup steps error:', err);
        return of(FETCHED_SIGNUPSTEP_INFO({ steps: [] }));
      }),
    );
  });

  getCompany$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType('FETCHED_COMPANY_DETAILS'),
      takeUntil(this.destroy$),
      withLatestFrom(this.store.select(COMPANY_INFO)),
      switchMap(([, companyInfo]: [any, any]) => {
        // Skip API call when company info is already in store (e.g. navigating from order summary)
        if (companyInfo?.compDetails?.uuid) {
          return of(companyInfo.compDetails);
        }
        // Skip HTTP call during SSR — blocks Zone.js stabilization
        if (!isPlatformBrowser(this.platformId)) {
          return of(
            ERROR_COMPANY_DETAILS({
              err: {
                details: { status: 0, errResponse: 'SSR skip' },
                status: true,
              },
            }),
          );
        }
        return this.appInit.getCompanyInformation(environment.BASE_URL);
      }),
      mergeMap((result: any) => {
        if (result?.type === 'P_ERRORCOMPANYDETAILS') {
          return of(result);
        }
        const companyPayLoad = result;
        this.store.dispatch(
          SELECT_LANGUAGE({
            lang: this.appInit.parseLanguage(companyPayLoad.primary_language),
          }),
        );
        this.store.dispatch({ type: 'FETCH_CURRENT_LOCATION' });
        return of(FETCHED_COMPANY_DETAILS({ companyDetails: companyPayLoad }));
      }),
      finalize(() => {
        // Finalize callback - can be used for cleanup if needed
      }),
      catchError((err) =>
        of(
          ERROR_COMPANY_DETAILS({
            err: {
              details: {
                status: err.originalError?.status,
                errResponse: err.originalError?.error,
              },
              status: true,
            },
          }),
        ),
      ),
    );
  });

  getTenantAndSubDetails$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType('FETCHED_TENANT_LIST'),
      takeUntil(this.destroy$),
      switchMap((param: any) =>
        this.openApiService.GetTenant(param.companyUuid, param.tenantUuid),
      ),
      mergeMap((tenantPayLoad) => {
        const countryPayload: Countries = this.getCountryState(tenantPayLoad);
        const statePayload: States = this.getStatesState(tenantPayLoad);
        const cityPayload: Cities = this.getCitiesState(tenantPayLoad);
        // Only access localStorage on the browser — it doesn't exist during SSR
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem('selectedTenant', JSON.stringify(tenantPayLoad));
        }
        return [
          FETCHED_TENANT_DETAILS({ selected_tenant: tenantPayLoad }),
          STATSIG_UPDATE_USER({ selected_tenant: tenantPayLoad }),
          FETCH_COUNTRY_MASTER_LIST({ countries: countryPayload.countries }),
          FETCH_COUNTRY_MASTER({ country: countryPayload.country }),
          FETCH_STATE_MASTER_LIST({ states: statePayload.states }),
          FETCH_STATE_MASTER({ state: statePayload.state }),
          FETCH_CITY_MASTER_LIST({ cities: cityPayload.cities }),
          FETCH_CITY_MASTER({ city: cityPayload.city }),
        ];
      }),
      catchError((err) => {
        // Only navigate to error page on browser platform to prevent hydration issues
        if (isPlatformBrowser(this.platformId)) {
          this.router.navigate(['/notfound/tenantnotfound']);
        }
        return of(
          ERROR_TENANT_LIST({
            err: {
              details: {
                status: err.originalError?.status,
                errResponse: err.originalError?.error,
              },
              status: true,
            },
          }),
        );
      }),
    );
  });

  getCurrentLocation$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType('FETCH_CURRENT_LOCATION'),
      takeUntil(this.destroy$),
      mergeMap(() => {
        // Skip external IP API calls during SSR — they can hang and block the render
        if (!isPlatformBrowser(this.platformId)) {
          return of(
            FETCHED_CURRENT_LOCATION({
              location: { lat: 0, lon: 0, status: 'ssr-skip' },
            }),
          );
        }
        return from(this.locationService.getPosition()).pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            // Cleanup location service if needed
          }),
        );
      }),
      map((res: any) => {
        // If already dispatched from the SSR skip branch, return as-is
        if (res.type) return res;
        return FETCHED_CURRENT_LOCATION({ location: res });
      }),
      catchError((err) => {
        console.warn('Location service error:', err);
        return of(ERROR_CURRENT_LOCATION({ error: err }));
      }),
    );
  });

  // HERE WE CAN Wait for different data states to get ready
  statsigUpdateUser$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(STATSIG_UPDATE_USER),
      takeUntil(this.destroy$),
      debounceTime(300),
      switchMap(({ selected_tenant: tenant }) => {
        // Skip Statsig initialization during SSR — it's a browser-only SDK
        // that makes network calls and returns Promises which Zone.js tracks,
        // causing the SSR render to hang indefinitely waiting for stabilization
        if (!isPlatformBrowser(this.platformId)) {
          return of(STATSIG_UPDATE_USER_SUCCESS({ user: null }));
        }
        return this.store.select(COMPANY_INFO).pipe(
          takeUntil(this.destroy$),
          map((company: any) => {
            try {
              const user = this.createStatsigUser(tenant, company);

              if (Statsig.initializeCalled()) {
                Statsig.updateUser(user);
              } else {
                Statsig.initialize(environment.STATSIG_SDK_KEY, user, {
                  environment: { tier: environment.ENVIRONMENT_NAME },
                });
              }

              return STATSIG_UPDATE_USER_SUCCESS({ user });
            } catch (error) {
              console.error('Statsig initialization error:', error);
              return STATSIG_UPDATE_USER_SUCCESS({ user: null });
            }
          }),
          finalize(() => {
            // Cleanup any pending statsig operations
          }),
        );
      }),
    );
  });

  // Helper method to create Statsig user
  private createStatsigUser(tenant: any, company: any): StatsigUser {
    return {
      userID: 0,
      email: '',
      userAgent:
        typeof window !== 'undefined' ? window.navigator.userAgent : '',
      appVersion: '1.0',
      custom: {
        comp_id: tenant.company_uuid,
        tenant_id: tenant.tenant_uuid,
        tent_status: '',
        ...this.getTenantCustomProperties(tenant, company),
      },
    };
  }

  private getTenantCustomProperties(tenant: any, company: any) {
    return {
      tenant_country: tenant.country,
      comp_purchase_portal_enable: company.compDetails.purchase_portal_enable,
      comp_member_portal_enable: company.compDetails.member_portal_enable,
      tent_pri_lang: tenant.primary_language,
      tent_sec_lang: tenant.secondary_language,
      tent_city: tenant.city,
      tent_state: tenant.state,
      tent_country: tenant.country,
    };
  }

  isRecurring(data: SubscriptionSignUpOrderSummaryResponse): boolean {
    if (!data?.subscription_category) return false;

    const category = String(data.subscription_category);
    return [
      PlanCategory.Contract.toString(),
      PlanCategory[PlanCategory.Contract],
      PlanCategory.PayAsYouGo.toString(),
      PlanCategory[PlanCategory.PayAsYouGo],
    ].includes(category);
  }

  private isPosTerminalPaymentMode(data: SubscriptionSignUpOrderSummaryResponse): boolean {
    if (data?.paid_via_payment_mode == null) {
      return false;
    }
    const mode = data.paid_via_payment_mode;
    return (
      mode === PaidViaPaymentMode.PosTerminal ||
      mode.toString() === PaidViaPaymentMode[PaidViaPaymentMode.PosTerminal] ||
      mode.toString() === 'PosTerminal'
    );
  }

  private isCartOnFilePaymentMode(data: SubscriptionSignUpOrderSummaryResponse): boolean {
    if (data?.paid_via_payment_mode == null) {
      return false;
    }
    const mode = data.paid_via_payment_mode;
    return (
      mode === PaidViaPaymentMode.CartOnFile ||
      mode.toString() === PaidViaPaymentMode[PaidViaPaymentMode.CartOnFile] ||
      mode.toString() === 'CartOnFile'
    );
  }

  private shouldShowPaymentStep(subsDetails: SubscriptionSignUpOrderSummaryResponse): boolean {
    if (this.isPosTerminalPaymentMode(subsDetails) && !subsDetails.is_token_generated) {
      return true;
    }

    if (this.isCartOnFilePaymentMode(subsDetails)) {
      return false;
    }

    if (this.isRecurring(subsDetails) && (!subsDetails.is_paid || !subsDetails.is_token_generated)) {
      return true;
    }
    if (!this.isRecurring(subsDetails) && !subsDetails.is_offline_payment && !subsDetails.is_paid) {
      return true;
    }
    return false;
  }
}
